import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { decks, type DiscoveredDeck } from '@/lib/api';
import { toast } from '@/lib/toast';
import { CardAutocomplete } from '@/components/CardAutocomplete';
import { VoiceDeckEntry } from '@/components/VoiceDeckEntry';
import { GuidedDeckTab } from '@/components/GuidedDeckTab';

interface AddDeckDialogProps {
  open: boolean;
  onClose: () => void;
  onDeckAdded: (deckId?: string) => void;
}

type TabId = 'guided' | 'url' | 'manual' | 'speak' | 'bulk';

const FORMATS = [
  { value: 'commander', label: 'Commander' },
  { value: 'standard', label: 'Standard' },
  { value: 'modern', label: 'Modern' },
  { value: 'legacy', label: 'Legacy' },
  { value: 'vintage', label: 'Vintage' },
  { value: 'pioneer', label: 'Pioneer' },
  { value: 'pauper', label: 'Pauper' },
];

// ── Tab sub-components ───────────────────────────────────────────────────

interface UrlImportTabProps {
  url: string;
  setUrl: (v: string) => void;
  urlLoading: boolean;
  urlError: string;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}

function UrlImportTab({ url, setUrl, urlLoading, urlError, onSubmit, onClose }: UrlImportTabProps) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3">
      <label htmlFor="add-deck-url" className="text-base text-on-surface-variant mb-0.5">Deck URL</label>
      <input
        id="add-deck-url"
        type="url"
        placeholder="https://www.moxfield.com/decks/... or https://archidekt.com/decks/..."
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        required
        className="input-field"
      />
      <small className="text-sm text-on-surface-variant opacity-70">
        Paste a Moxfield or Archidekt deck URL
      </small>

      {urlError && <div className="text-error-text p-3 bg-status-error-bg rounded-sm text-base">{urlError}</div>}

      <div className="flex gap-2 justify-end mt-1">
        <button type="button" onClick={onClose} className="btn-ghost">
          Cancel
        </button>
        <button
          type="submit"
          disabled={urlLoading}
          className="btn-primary"
          style={{ opacity: urlLoading ? 0.7 : 1, cursor: urlLoading ? 'not-allowed' : 'pointer' }}
        >
          {urlLoading ? 'Importing...' : 'Import'}
        </button>
      </div>
    </form>
  );
}

interface ManualEntryTabProps {
  deckName: string;
  setDeckName: (v: string) => void;
  commander: string;
  setCommander: (v: string) => void;
  format: string;
  setFormat: (v: string) => void;
  decklistText: string;
  setDecklistText: (v: string) => void;
  manualLoading: boolean;
  manualError: string;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}

function ManualEntryTab({
  deckName, setDeckName, commander, setCommander, format, setFormat,
  decklistText, setDecklistText, manualLoading, manualError, onSubmit, onClose,
}: ManualEntryTabProps) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3">
      <div>
        <label className="text-base text-on-surface-variant mb-0.5">Deck Name *</label>
        <input
          type="text"
          placeholder="My awesome deck"
          value={deckName}
          onChange={(e) => setDeckName(e.target.value)}
          required
          className="input-field"
        />
      </div>

      <div>
        <label className="text-base text-on-surface-variant mb-0.5">Commander (optional)</label>
        <CardAutocomplete
          value={commander}
          onChange={setCommander}
          placeholder="Commander name..."
          tabComplete={true}
        />
      </div>

      <div>
        <label className="text-base text-on-surface-variant mb-0.5">Format</label>
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          className="input-field cursor-pointer"
        >
          {FORMATS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-base text-on-surface-variant mb-0.5">Decklist *</label>
        <CardAutocomplete
          value={decklistText}
          onChange={setDecklistText}
          multiline
          triggerMode="[["
          tabComplete={true}
          rows={10}
          placeholder={'1 Sol Ring\n1 Command Tower\n1 Arcane Signet\n...'}
          style={{ fontFamily: 'monospace', resize: 'vertical' }}
        />
        <small className="text-sm text-on-surface-variant opacity-70">
          Format: quantity cardname (one per line). Type [[ or Tab to autocomplete card names.
        </small>
      </div>

      {manualError && <div className="text-error-text p-3 bg-status-error-bg rounded-sm text-base">{manualError}</div>}

      <div className="flex gap-2 justify-end mt-1">
        <button type="button" onClick={onClose} className="btn-ghost">
          Cancel
        </button>
        <button
          type="submit"
          disabled={manualLoading}
          className="btn-primary"
          style={{
            opacity: manualLoading ? 0.7 : 1,
            cursor: manualLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {manualLoading ? 'Adding...' : 'Add Deck'}
        </button>
      </div>
    </form>
  );
}

interface BulkImportTabProps {
  bulkPlatform: 'moxfield' | 'archidekt';
  setBulkPlatform: (v: 'moxfield' | 'archidekt') => void;
  bulkUsername: string;
  setBulkUsername: (v: string) => void;
  bulkSearching: boolean;
  bulkError: string;
  discoveredDecks: DiscoveredDeck[];
  selectedUrls: Set<string>;
  bulkImporting: boolean;
  bulkProgress: { done: number; total: number };
  onFind: () => void;
  onToggleDeck: (url: string) => void;
  onSelectAllNew: () => void;
  onDeselectAll: () => void;
  onBulkImport: () => void;
  onClose: () => void;
}

function BulkImportTab({
  bulkPlatform, setBulkPlatform, bulkUsername, setBulkUsername,
  bulkSearching, bulkError, discoveredDecks, selectedUrls,
  bulkImporting, bulkProgress, onFind, onToggleDeck,
  onSelectAllNew, onDeselectAll, onBulkImport, onClose,
}: BulkImportTabProps) {
  const newDeckCount = discoveredDecks.filter((d) => !d.already_imported).length;

  return (
    <div className="flex flex-col gap-4">
      {/* Platform + Username */}
      <div className="flex gap-3 items-end flex-wrap">
        <div className="flex flex-col gap-1 min-w-[140px]">
          <label className="text-base text-on-surface-variant mb-0.5">Platform</label>
          <div className="flex gap-0 rounded-sm overflow-hidden border border-outline-variant">
            {(['moxfield', 'archidekt'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setBulkPlatform(p)}
                className="flex-1 py-2 px-3.5 border-none text-base cursor-pointer transition-[background] duration-150 capitalize"
                style={{
                  background: bulkPlatform === p ? 'var(--primary)' : 'var(--surface-container-high)',
                  color: bulkPlatform === p ? 'var(--on-primary)' : 'var(--on-surface)',
                  fontWeight: bulkPlatform === p ? 600 : 400,
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 min-w-[160px]">
          <label className="text-base text-on-surface-variant mb-0.5">Username</label>
          <input
            type="text"
            placeholder="your username"
            value={bulkUsername}
            onChange={(e) => setBulkUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onFind()}
            className="input-field"
          />
        </div>

        <button
          type="button"
          onClick={onFind}
          disabled={bulkSearching || !bulkUsername.trim()}
          className="btn-primary whitespace-nowrap"
          style={{
            opacity: bulkSearching || !bulkUsername.trim() ? 0.7 : 1,
            cursor: bulkSearching || !bulkUsername.trim() ? 'not-allowed' : 'pointer',
          }}
        >
          {bulkSearching ? 'Searching...' : 'Find Decks'}
        </button>
      </div>

      {bulkError && <div className="text-error-text p-3 bg-status-error-bg rounded-sm text-base">{bulkError}</div>}

      {/* Discovery results */}
      {discoveredDecks.length > 0 && (
        <div className="flex flex-col gap-2">
          {/* Summary + select controls */}
          <div className="flex justify-between items-center flex-wrap gap-2">
            <span className="text-base text-on-surface-variant">
              Found {discoveredDecks.length} deck{discoveredDecks.length !== 1 ? 's' : ''} —{' '}
              {newDeckCount} new, {discoveredDecks.length - newDeckCount} already imported
            </span>
            <div className="flex gap-2">
              <button type="button" onClick={onSelectAllNew} className="btn-ghost py-1 px-2.5 text-sm">
                Select all new
              </button>
              <button type="button" onClick={onDeselectAll} className="btn-ghost py-1 px-2.5 text-sm">
                Deselect all
              </button>
            </div>
          </div>

          {/* Deck list */}
          <div className="max-h-[300px] overflow-y-auto flex flex-col gap-1">
            {discoveredDecks.map((d) => {
              const disabled = d.already_imported;
              const checked = selectedUrls.has(d.url);
              const meta = [d.format, d.commander].filter(Boolean).join(' \u00b7 ');
              return (
                <label
                  key={d.url}
                  className="flex items-start gap-2.5 py-2 px-2.5 rounded-sm bg-surface-container-high"
                  style={{
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: disabled ? 0.45 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={disabled ? false : checked}
                    disabled={disabled}
                    onChange={() => !disabled && onToggleDeck(d.url)}
                    className="accent-primary mt-[3px] shrink-0"
                  />
                  <span className="min-w-0">
                    <span className="font-medium break-words text-on-surface text-md">
                      {d.name || d.deck_id || 'Unnamed'}
                    </span>
                    {meta && (
                      <span className="block text-sm text-on-surface-variant opacity-70">
                        {meta}
                      </span>
                    )}
                    {disabled && (
                      <span className="block text-xs text-primary opacity-80">
                        Already imported
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>

          {/* Bulk import progress */}
          {bulkImporting && (
            <div className="flex flex-col gap-1.5">
              <span className="text-base text-on-surface-variant">
                Importing {bulkProgress.done} / {bulkProgress.total}...
              </span>
              <div className="w-full h-1.5 bg-surface-container-high rounded-[3px] overflow-hidden">
                <div
                  className="h-full bg-primary rounded-[3px] transition-[width] duration-300 ease-out"
                  style={{
                    width: `${bulkProgress.total > 0 ? (bulkProgress.done / bulkProgress.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Import Selected button */}
          {!bulkImporting && selectedUrls.size > 0 && (
            <div className="flex justify-end mt-1">
              <button type="button" onClick={onBulkImport} className="btn-primary">
                Import Selected ({selectedUrls.size})
              </button>
            </div>
          )}
        </div>
      )}

      {/* Close button at bottom when no results yet */}
      {discoveredDecks.length === 0 && !bulkSearching && (
        <div className="flex justify-end mt-1">
          <button type="button" onClick={onClose} className="btn-ghost">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────

export function AddDeckDialog({ open, onClose, onDeckAdded }: AddDeckDialogProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('guided');

  // Tab 1 – URL Import
  const [url, setUrl] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlError, setUrlError] = useState('');

  // Tab 2 – Manual Entry
  const [deckName, setDeckName] = useState('');
  const [commander, setCommander] = useState('');
  const [format, setFormat] = useState('commander');
  const [decklistText, setDecklistText] = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState('');

  // Tab 3 – Bulk Import
  const [bulkPlatform, setBulkPlatform] = useState<'moxfield' | 'archidekt'>('moxfield');
  const [bulkUsername, setBulkUsername] = useState('');
  const [bulkSearching, setBulkSearching] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [discoveredDecks, setDiscoveredDecks] = useState<DiscoveredDeck[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  const backdropRef = useRef<HTMLDivElement>(null);

  // ── Reset helpers ───────────────────────────────────────────────────────

  const resetAll = useCallback(() => {
    setUrl('');
    setUrlLoading(false);
    setUrlError('');
    setDeckName('');
    setCommander('');
    setFormat('commander');
    setDecklistText('');
    setManualLoading(false);
    setManualError('');
    setBulkPlatform('moxfield');
    setBulkUsername('');
    setBulkSearching(false);
    setBulkError('');
    setDiscoveredDecks([]);
    setSelectedUrls(new Set());
    setBulkImporting(false);
    setBulkProgress({ done: 0, total: 0 });
    setActiveTab('guided');
  }, []);

  const handleClose = useCallback(() => {
    resetAll();
    onClose();
  }, [onClose, resetAll]);

  // ── Tab 1: URL Import ───────────────────────────────────────────────────

  const handleUrlImport = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) return;
    setUrlLoading(true);
    setUrlError('');
    try {
      const newDeck = await decks.importUrl(trimmed);
      if (newDeck.credits_warning) {
        toast.warn(
          'Deck imported — analysis skipped: free tier limit reached.',
          { action: { label: 'Upgrade to Pro', href: '/settings' } },
        );
      } else {
        toast.success('Deck imported!');
      }
      handleClose();
      navigate(`/deck/${newDeck.id}`);
      onDeckAdded(newDeck.id);
    } catch (err) {
      setUrlError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setUrlLoading(false);
    }
  };

  // ── Tab 2: Manual Entry ─────────────────────────────────────────────────

  const handleManualAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deckName.trim() || !decklistText.trim()) return;
    setManualLoading(true);
    setManualError('');
    try {
      const newDeck = await decks.addManual(
        deckName.trim(),
        format,
        decklistText.trim(),
        commander.trim() || undefined,
      );
      toast.success('Deck added!');
      onDeckAdded(newDeck.id);
      handleClose();
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Failed to add deck');
    } finally {
      setManualLoading(false);
    }
  };

  // ── Tab 3: Bulk Import ──────────────────────────────────────────────────

  const handleBulkFind = async () => {
    const username = bulkUsername.trim();
    if (!username) return;
    setBulkSearching(true);
    setBulkError('');
    setDiscoveredDecks([]);
    setSelectedUrls(new Set());
    try {
      const data = await decks.bulkDiscover(bulkPlatform, username);
      const found = data.decks || [];
      setDiscoveredDecks(found);
      // Pre-select all non-imported decks
      const newUrls = new Set(found.filter((d) => !d.already_imported).map((d) => d.url));
      setSelectedUrls(newUrls);
      if (found.length === 0) {
        setBulkError('No decks found for that username.');
      }
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Discovery failed');
    } finally {
      setBulkSearching(false);
    }
  };

  const toggleDeck = (deckUrl: string) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(deckUrl)) next.delete(deckUrl);
      else next.add(deckUrl);
      return next;
    });
  };

  const selectAllNew = () => {
    const newUrls = discoveredDecks.filter((d) => !d.already_imported).map((d) => d.url);
    setSelectedUrls(new Set(newUrls));
  };

  const deselectAll = () => {
    setSelectedUrls(new Set());
  };

  const handleBulkImport = async () => {
    const urls = Array.from(selectedUrls);
    if (!urls.length) return;
    setBulkImporting(true);
    setBulkProgress({ done: 0, total: urls.length });

    let done = 0;
    let failed = 0;
    let hitCreditLimit = false;
    for (const deckUrl of urls) {
      try {
        const result = await decks.importUrl(deckUrl);
        done++;
        if (result.credits_warning) {
          hitCreditLimit = true;
          break;
        }
      } catch {
        failed++;
      }
      setBulkProgress({ done: done + failed, total: urls.length });
    }

    setBulkImporting(false);
    if (hitCreditLimit) {
      toast.warn(`Imported ${done} deck${done !== 1 ? 's' : ''} — analysis skipped for remaining: free tier limit reached.`);
    } else if (done > 0) {
      toast.success(`Imported ${done} deck${done !== 1 ? 's' : ''}${failed ? ` (${failed} failed)` : ''}`);
    }
    if (failed > 0 && done === 0) {
      toast.error('All imports failed');
    }
    if (done > 0) {
      onDeckAdded();
      handleClose();
    }
  };

  // ── Focus dialog when it opens ──────────────────────────────────────────
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [open]);

  // Escape + global hotkey guard are handled via onKeyDown on the dialog panel below.

  // ── Render ──────────────────────────────────────────────────────────────

  if (!open) return null;

  return (
    <div
      ref={backdropRef}
      onClick={(e) => e.target === backdropRef.current && handleClose()}
      className="fixed inset-0 z-[2000] bg-black/60 flex items-center justify-center p-6"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Add deck"
        tabIndex={-1}
        className="bg-surface-container rounded-lg px-7 pt-6 pb-7 w-full max-w-[600px] max-h-[90vh] overflow-y-auto relative"
        onKeyDown={(e) => {
          // Handle Escape close here (before stopping propagation to global handlers).
          if (e.key === 'Escape' && activeTab !== 'speak') handleClose();
          // Stop all keystrokes from leaking out to global hotkey handlers
          // (e.g. 'g' opening navigation, 'l' opening card lookup).
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="m-0 text-on-surface text-xl font-bold">Add Deck</h2>
          <button
            onClick={handleClose}
            aria-label="Close"
            className="bg-transparent border-none text-on-surface-variant cursor-pointer text-[20px] leading-none p-1"
          >
            &#10005;
          </button>
        </div>

        {/* Tab Bar */}
        <div
          role="tablist"
          className="flex gap-0 border-b border-outline-variant mb-5"
        >
          {([
            { id: 'guided' as TabId, icon: 'auto_awesome', label: 'Guided' },
            { id: 'url' as TabId, icon: 'link', label: 'Import URL' },
            { id: 'manual' as TabId, icon: 'edit_note', label: 'Text Paste' },
            { id: 'speak' as TabId, icon: 'mic', label: 'Voice' },
            { id: 'bulk' as TabId, icon: 'library_add', label: 'Bulk Import' },
          ]).map((tab) => (
            <button
              key={tab.id}
              id={`add-deck-tab-${tab.id}`}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 py-2.5 bg-transparent border-none text-md cursor-pointer transition-[color,border-color] duration-150 flex flex-col items-center gap-0.5"
              style={{
                borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
                color: activeTab === tab.id ? 'var(--primary)' : 'var(--on-surface-variant)',
                fontWeight: activeTab === tab.id ? 600 : 400,
              }}
            >
              <span className="material-icons text-base leading-none">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div role="tabpanel" aria-labelledby={`add-deck-tab-${activeTab}`}>
        {activeTab === 'guided' && (
          <GuidedDeckTab
            onCreated={(deckId) => {
              handleClose();
              navigate(`/deck/${deckId}?tab=build`);
              onDeckAdded(deckId);
            }}
            onClose={handleClose}
          />
        )}
        {activeTab === 'url' && (
          <UrlImportTab
            url={url}
            setUrl={setUrl}
            urlLoading={urlLoading}
            urlError={urlError}
            onSubmit={handleUrlImport}
            onClose={handleClose}
          />
        )}

        {activeTab === 'manual' && (
          <ManualEntryTab
            deckName={deckName}
            setDeckName={setDeckName}
            commander={commander}
            setCommander={setCommander}
            format={format}
            setFormat={setFormat}
            decklistText={decklistText}
            setDecklistText={setDecklistText}
            manualLoading={manualLoading}
            manualError={manualError}
            onSubmit={handleManualAdd}
            onClose={handleClose}
          />
        )}

        {activeTab === 'speak' && (
          <VoiceDeckEntry
            onComplete={(decklist, commanderName) => {
              setDecklistText(decklist);
              if (commanderName && !deckName) setDeckName(commanderName);
              if (commanderName) setCommander(commanderName);
              setActiveTab('manual');
            }}
          />
        )}

        {activeTab === 'bulk' && (
          <BulkImportTab
            bulkPlatform={bulkPlatform}
            setBulkPlatform={setBulkPlatform}
            bulkUsername={bulkUsername}
            setBulkUsername={setBulkUsername}
            bulkSearching={bulkSearching}
            bulkError={bulkError}
            discoveredDecks={discoveredDecks}
            selectedUrls={selectedUrls}
            bulkImporting={bulkImporting}
            bulkProgress={bulkProgress}
            onFind={handleBulkFind}
            onToggleDeck={toggleDeck}
            onSelectAllNew={selectAllNew}
            onDeselectAll={deselectAll}
            onBulkImport={handleBulkImport}
            onClose={handleClose}
          />
        )}
        </div>
      </div>
    </div>
  );
}
